import { omit, uniq } from 'ramda'
import { ActionsObservable, combineEpics, ofType } from 'redux-observable'
import { forkJoin, of } from 'rxjs'
import { catchError, map, mergeMap, pluck, startWith } from 'rxjs/operators'

import * as conversationSummaryAPI from 'api/report/conversationSummary'
import { EXPORT_FUNCTON_FOR_METRIC } from 'api/report/conversationSummary/exporters'
import * as userActivityAPI from 'api/report/userActivity'
import * as OrgSelectors from 'modules/org/selectors'
// import * as RulesActions from 'app/modules/rules/actions'
import * as PlaybooksActions from 'modules/playbooks/actions'
import { hasConversationRatingDropGate, hasDemoOrgGate } from 'utils/access'
import { fromRequest } from 'utils/api'
import { createCSVDownload, csvFilenameFromQuery } from 'utils/csv'
import { keyBy } from 'utils/ramda'
import { selectState } from 'utils/redux'

import * as actions from './actions'
import * as DemoConstants from './demodata'

const fetchRepPerformanceReport = (queryConfig, options) => {
  return forkJoin([
    fromRequest(conversationSummaryAPI.fetchSimpleReport(queryConfig, options)),
    fromRequest(
      conversationSummaryAPI.fetchSimpleReport(
        {
          ...queryConfig,
          metric: 'liveChatRatingsByRep',
        },
        options
      )
    ),
  ])
}

const mergeRepPerformanceData = (repPerformance, repRatings) => {
  const ratingsByAgentId = keyBy(repRatings, 'agentId')

  return repPerformance.map((row) => {
    return {
      ...row,
      ...omit(['agentId'], ratingsByAgentId[row.user]),
    }
  })
}

const fetchConversationsData = (action$: ActionsObservable<any>) => {
  return action$.pipe(
    ofType(actions.fetchConversationsData.toString()),
    pluck('payload'),
    mergeMap(({ queryConfig, options }) => {
      if (hasDemoOrgGate()) {
        return DemoConstants.fetchConversationsData(queryConfig).pipe(
          mergeMap((response) =>
            parseFetchConversationData(response, queryConfig)
          ),
          startWith(actions.fetchConversationsDataPending(queryConfig)),
          catchError((error) => {
            return of(
              actions.fetchConversationsDataFailed({ queryConfig, error })
            )
          })
        )
      } else {
        if (
          hasConversationRatingDropGate() &&
          queryConfig.metric === 'repPerformance'
        ) {
          return fetchRepPerformanceReport(queryConfig, options).pipe(
            mergeMap(([repPerformance, repRatings]: any[]) => {
              const mergedResult = mergeRepPerformanceData(
                repPerformance,
                repRatings
              )
              return parseFetchConversationData(mergedResult, queryConfig)
            }),
            startWith(actions.fetchConversationsDataPending(queryConfig)),
            catchError((error) => {
              console.error(error)
              return of(
                actions.fetchConversationsDataFailed({ queryConfig, error })
              )
            })
          )
        }

        return fromRequest(
          conversationSummaryAPI.fetchSimpleReport(queryConfig, options)
        ).pipe(
          mergeMap((response) =>
            parseFetchConversationData(response, queryConfig)
          ),
          startWith(actions.fetchConversationsDataPending(queryConfig)),
          catchError((error) => {
            console.error(error)
            return of(
              actions.fetchConversationsDataFailed({ queryConfig, error })
            )
          })
        )
      }
    })
  )
}

const fetchAvailabilityData = (action$: ActionsObservable<any>) => {
  return action$.pipe(
    ofType(actions.fetchAvailabilityData.toString()),
    pluck('payload'),
    mergeMap(({ queryConfig }) => {
      if (hasDemoOrgGate()) {
        return DemoConstants.fetchAvailabilityData().pipe(
          map((response) =>
            actions.fetchAvailabilityDataSuccess({ queryConfig, response })
          ),
          startWith(actions.fetchAvailabilityDataPending(queryConfig)),
          catchError((error) => {
            return of(
              actions.fetchAvailabilityDataFailed({ queryConfig, error })
            )
          })
        )
      } else {
        return fromRequest(
          userActivityAPI.fetchAvailabilityData(queryConfig)
        ).pipe(
          map((response) =>
            actions.fetchAvailabilityDataSuccess({ queryConfig, response })
          ),
          startWith(actions.fetchAvailabilityDataPending(queryConfig)),
          catchError((error) => {
            return of(
              actions.fetchAvailabilityDataFailed({ queryConfig, error })
            )
          })
        )
      }
    })
  )
}

const parseFetchConversationData = (response, queryConfig) => {
  const actionPayload = [
    actions.fetchConversationsDataSuccess({ queryConfig, response }),
  ]
  // if (queryConfig.metric === 'routingActionRoutingRules') {
  //   const ruleIds = response.mergeMap(r => r.type)
  //   actionPayload.push(RulesActions.fetchRulesByIds(ruleIds))
  // }
  if (
    queryConfig.metric === 'routingActionPlaybooks' ||
    queryConfig.metric === 'routingActionPlaybookActions'
  ) {
    const playbookIds = response.map((r) => r.playbookId)
    actionPayload.push(PlaybooksActions.fetchPlaybooksByIds(uniq(playbookIds)))
  }
  return actionPayload
}

const exportConversationData = (action$, state$) =>
  action$.pipe(
    ofType(actions.exportConversationData.toString()),
    pluck('payload'),
    mergeMap(({ queryConfig, options }) => {
      if (
        hasConversationRatingDropGate() &&
        queryConfig.metric === 'repPerformance'
      ) {
        return fetchRepPerformanceReport(queryConfig, options).pipe(
          mergeMap(([repPerformance, repRatings]: any[]) => {
            const mergedResult = mergeRepPerformanceData(
              repPerformance,
              repRatings
            )
            const orgUsers = OrgSelectors.unfilteredOrgUsersById(state$.value)
            const classifications = selectState(state$.value, 'classifications')
            const exportCSV = EXPORT_FUNCTON_FOR_METRIC[queryConfig.metric]
            exportCSV(
              mergedResult,
              orgUsers,
              csvFilenameFromQuery(queryConfig),
              classifications
            )
            return of(
              actions.exportConversationDataSuccess({
                queryConfig,
                mergedResult,
              })
            )
          }),

          catchError((error) => {
            console.error('Error while exporting CSV:', error)
            return of(
              actions.exportConversationDataFailed({ queryConfig, error })
            )
          })
        )
      }

      return fromRequest(
        conversationSummaryAPI.fetchSimpleReport(queryConfig, options)
      ).pipe(
        map((response) => {
          const orgUsers = OrgSelectors.unfilteredOrgUsersById(state$.value)
          const classifications = selectState(state$.value, 'classifications')
          const exportCSV = EXPORT_FUNCTON_FOR_METRIC[queryConfig.metric]
          exportCSV(
            response,
            orgUsers,
            csvFilenameFromQuery(queryConfig),
            classifications
          )
          return actions.exportConversationDataSuccess({
            queryConfig,
            response,
          })
        }),
        catchError((error) => {
          console.error('Error while exporting CSV:', error)
          return of(
            actions.exportConversationDataFailed({ queryConfig, error })
          )
        })
      )
    })
  )

const exportAllConversationData = (action$: ActionsObservable<any>) =>
  action$.pipe(
    ofType(actions.exportAllConversationData.toString()),
    pluck('payload'),
    mergeMap(({ queryConfig, options }) =>
      fromRequest(
        conversationSummaryAPI.fetchReportCSV(queryConfig, options)
      ).pipe(
        map((response) => {
          createCSVDownload(response, csvFilenameFromQuery(queryConfig))
          return actions.exportConversationDataSuccess({
            queryConfig,
            response,
          })
        }),
        catchError((error) => {
          console.error('Error while exporting CSV:', error)
          return of(
            actions.exportConversationDataFailed({ queryConfig, error })
          )
        })
      )
    )
  )

export default combineEpics(
  fetchConversationsData,
  exportConversationData,
  exportAllConversationData,
  fetchAvailabilityData
)
